import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    MakeAudiencePublic as MakeAudiencePublicMutation,
    MakeAudiencePublicVariables as MakeAudiencePublicVariables,
    GetAudiencesForPreferenceCenter as GetAudiencesForPreferenceCenterQuery,
    AudienceUpdateInput,
} from 'nutshell-graphql-types';

import MAKE_AUDIENCE_PUBLIC from '../mutations/make-audience-public.graphql';
import GET_AUDIENCES from '../queries/get-audiences.graphql';

export const useMakeAudiencePublic = (): {
    makeAudiencePublic: (arg1: AudienceUpdateInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [makeAudiencePublic, {loading, error}] = useMutation<
        MakeAudiencePublicMutation,
        MakeAudiencePublicVariables
    >(MAKE_AUDIENCE_PUBLIC);

    return {
        makeAudiencePublic: (input: AudienceUpdateInput) =>
            makeAudiencePublic({
                variables: {input},
                update: (proxy, mutationResults) => {
                    const cacheResult: GetAudiencesForPreferenceCenterQuery | null | undefined =
                        proxy.readQuery({
                            query: GET_AUDIENCES,
                            variables: {
                                first: 10,
                                sort: 'NAME_ASC',
                                audienceTypeFilter: 'ONLY_PUBLIC',
                            },
                        });

                    const audience =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.audienceUpdate &&
                        mutationResults.data.audienceUpdate.audience;

                    if (!audience) return;

                    const newEdge = {
                        node: audience,
                        cursor: audience.id,
                        __typename: 'AudiencesEdge',
                    } as const;

                    if (
                        cacheResult &&
                        !cacheResult.audiences.edges.find((edge) => edge.node.id === audience.id)
                    ) {
                        const data = {
                            audiences: {
                                ...cacheResult.audiences,
                                edges: cacheResult.audiences.edges
                                    ? [...cacheResult.audiences.edges, newEdge].sort((a, b) => {
                                          return a.node.name.localeCompare(b.node.name);
                                      })
                                    : [newEdge],
                                totalCount: cacheResult.audiences.totalCount + 1,
                            },
                        } as const;

                        proxy.writeQuery({
                            query: GET_AUDIENCES,
                            variables: {
                                first: 10,
                                sort: 'NAME_ASC',
                                audienceTypeFilter: 'ONLY_PUBLIC',
                            },
                            data,
                        });
                    }
                },
            }),
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
